import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { ConnectedApplications } from '~src/connectedApplications/resources/ConnectedApplicationsResource';
import { DataConnections } from '~src/connectivity/resources/DataConnections';
import { RunFlowTaskInfo } from '~src/flow/models/RunFlowTaskInfo';
import { RunFlowTasks } from '~src/flow/resources/RunFlowTasks';
import { LinkedTasks } from '~src/linkedTasks/resources/LinkedTasks';
import { PublishedConnectionTables } from '~src/publishedConnectionTable/resources/PublishedConnectionTables';
import { ResourceGovernanceService } from '~src/schedule/types/ResourceGovernanceService';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';

import { AvailableActionsForEntityRequest } from '../../app/actions/AppActionTypes';
import { requestFailed } from '../../app/actions/RequestFailedAction';
import { BackgroundJobsService } from '../../backgroundJob/types/BackgroundJobs';
import { Databases } from '../../catalog/resources/Databases';
import { Tables } from '../../catalog/resources/Tables';
import { Messages } from '../../codegen/Localize';
import { DataRoles } from '../../dataRole/resources/Dataroles';
import { getCommonDatasourceInfo } from '../../datasource/models/mappers/CommonDatasourceInfo';
import { DataSources } from '../../datasource/resources/Datasources';
import { Flows } from '../../flow/resources/Flows';
import { Lenses } from '../../lens/resources/Lenses';
import { Metrics } from '../../metrics/resources/Metrics';
import { PredictiveModels } from '../../predictiveModel/resources/PredictiveModels';
import { Projects } from '../../projects/resources/Projects';
import { PublishedConnections } from '../../publishedConnection/resources/PublishedConnections';
import { IActionResource } from '../../ts/resources/Resource';
import { CustomViews } from '../../ts/ServerApi/CustomViews/CustomViews';
import { ContentDraftStatus } from '../../ts/ServerApi/types/ContentDraft';
import { Views } from '../../views/resources/Views';
import { Workbooks } from '../../workbook/resources/Workbooks';
import { availableActionForEntityRequestSucceeded, AvailableActionsForEntityRequestAction } from '../actions/AvailableActionsForEntityActions';
import { ExplorableItemType } from '../models/ContentType';
import { IContentInfo } from '../models/IContentInfo';
import { AvailableActions } from '../state/AvailableActionsForEntityState';
import { SpaceInfo } from '../state/SpaceInfo';
import { getCorrectEntityIdForActions } from './SpacesSagaHelpers';

const GetActionResourceForContentType: Partial<Record<ExplorableItemType, () => IActionResource<AvailableActions>>> = {
  workbook: () => Workbooks,
  project: () => Projects,
  datasource: () => DataSources,
  dataRole: () => DataRoles,
  lens: () => Lenses,
  view: () => Views,
  connectedApplication: () => ConnectedApplications,
  dataconnection: () => DataConnections,
  flow: () => Flows,
  backgroundJob: () => BackgroundJobsService,
  table: () => Tables,
  tableWithCrass: () => Tables,
  database: () => Databases,
  databaseWithCrass: () => Databases,
  metric: () => Metrics,
  virtualConnection: () => PublishedConnections,
  virtualConnectionTable: () => PublishedConnectionTables,
  customView: () => CustomViews,
  resourceGovernanceSetting: () => ResourceGovernanceService,
  predictiveModel: () => PredictiveModels,
  runFlowTask: () => RunFlowTasks,
};

function getResourceContentId(
  entityType: ExplorableItemType,
  entityId: string,
  spaceInfo: SpaceInfo,
): string {
  if (entityType === 'extractTask') {
    return spaceInfo.item.id;
  }
  return entityId;
}

function getResourceContentType(
  entityType: ExplorableItemType,
  spaceInfo: SpaceInfo
): ExplorableItemType {
  if (entityType === 'extractTask') {
    return spaceInfo.item.contentType as ExplorableItemType;
  }
  return entityType;
}

/**
 * We request actions only for the ExplorableItemTypes that have an API call.
 * For other types there aren't APIs that retrieve the allowed actions;
 * instead, we determine allowed actions by looking at properties of those items and the site role.
 */
export function fetchAvailableActionsForEntity(
  entityType: ExplorableItemType,
  entityId: string,
  spaceInfo: SpaceInfo
): Promise<AvailableActions> {
  const resourceContentId = getResourceContentId(entityType, entityId, spaceInfo);
  const resourceContentType = getResourceContentType(entityType, spaceInfo);
  const actionResourceGetter = GetActionResourceForContentType[resourceContentType];
  const actionResource = actionResourceGetter && actionResourceGetter();
  if (actionResource) {
    return actionResource.actions(resourceContentId);
  }
  return null;
}

function fetchAvailableActionsForFlowDraft(draftId: string): Promise<AvailableActions> {
  return Flows.draftActions(draftId);
}

function fetchAvailableActionsForLinkedTask(id: string): Promise<AvailableActions> {
  return LinkedTasks.actions(id);
}

export function* processAvailableActionsForEntityRequest(action: AvailableActionsForEntityRequestAction) {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    const commonDatasource = getCommonDatasourceInfo(action.item as IContentInfo<'datasource'>);

    if (action.item.contentType === 'datasource' && DataSources.isEmbedded(commonDatasource)) {
      yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, null));
    } else if (action.item.contentType === 'flow'
      && (action.item as IContentInfo<'flow'>).draftStatus === ContentDraftStatus.unpublished) {
      // flow drafts that have never been published need to call getFlowDraftActions instead of getFlowActions
      const availableActions = yield call(fetchAvailableActionsForFlowDraft, action.item.id);
      yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, availableActions));
    } else if (action.item.contentType === 'runFlowTask') {
        const runFlowTask = action.item as RunFlowTaskInfo;
        if (runFlowTask.taskType === 'FlowTask') {
          // use the flowId of the task
          const availableActions = yield call(fetchAvailableActionsForEntity, action.item.contentType, runFlowTask.flowId,
            spaceInfo);
          yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, availableActions));
        } else if (runFlowTask.taskType === 'LinkedTask') {
          // call separate API for linked task action permissions
          const availableActions = yield call(fetchAvailableActionsForLinkedTask, runFlowTask.linkedTaskId);
          yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, availableActions));
        }
    } else {
      let entityIdForActions = yield call(getCorrectEntityIdForActions, action.item.contentType, action.item.id);

      // Hack to use `vizportalId` field for Database & Table
      // TFS912863: Remove this hack once we switched to use vizportalId as primary ID for database & table
      if (action.item.contentType === 'table' ||
          action.item.contentType === 'tableWithCrass' ||
          action.item.contentType === 'database' ||
          action.item.contentType === 'databaseWithCrass') {
        entityIdForActions = action.item.vizportalId;
      }

      const availableActions = yield call(fetchAvailableActionsForEntity, action.item.contentType, entityIdForActions,
          spaceInfo);
      yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, availableActions));
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootAvailableActionsForEntitySaga(): SagaIterator {
  yield takeLatest(AvailableActionsForEntityRequest, processAvailableActionsForEntityRequest);
}
